<template>
  <div class="fts-list-view-form" v-if="isShow">
    <div class="list-view-form__header">
      <div
        class="form-header__left"
      >  
        <div class="form-header__uppr">
          <div class="form-header__activity" v-if="selectedActivity && selectedActivity.name">
            {{selectedActivity.name}}
          </div>
          <div class="form-header__module">{{moduleName}}</div>
        </div>

        <div class="form-header__lowr">
          <!-- audit -->
          <fts-tippy
            :options="{ placement: 'bottom-start' }" 
            append-trigger-class="audit-group__trigger" 
            append-content-class="audit-group__content" 
            class="fts-audit-group form-header__audit" 
            v-if="header && header.audit"
          >
            <template>
              <fts-profile 
                :user-name="header.audit.updatedBy" 
                :profile-photo-size="14"
              />
              <div class="audit-group-date">
                {{moment(header.audit.updatedDate).fromNow()}}
              </div>
            </template>

            <template slot="content">
              <fts-profile :profilePhotoSize="26" :user-name="header.audit.updatedBy" class="audit-group-wrapper">
                <div slot="profileName" slot-scope="{ userProfileName }" class="audit-group-info">
                  <div>{{userProfileName}}</div>
                  <div class="audit-group-date">updated on {{moment(header.audit.updatedDate).format('llll')}}</div>
                </div>
              </fts-profile>

              <fts-profile :profilePhotoSize="26" :user-name="header.audit.createdBy" class="audit-group-wrapper">
                <div slot="profileName" slot-scope="{ userProfileName }" class="audit-group-info">
                  <div>{{userProfileName}}</div>
                  <div class="audit-group-date">created on {{moment(header.audit.createdDate).format('llll')}}</div>
                </div>
              </fts-profile>
            </template>
          </fts-tippy>
        </div>
      </div>

      <div class="form-header__right">
        <!-- form sync -->
        <fts-form-sync 
          :synchronize="$refs.activityClassification.synchronize" 
          class="form-header__sync form-header__item"
          v-if="
            selectedActivity &&
            !(['assign', 'changeStatus', 'duplicate', 'delete'].indexOf(selectedActivity.id) > -1) &&
            $refs.activityClassification
          "
        ></fts-form-sync>
        <!-- state -->
        <fts-state 
          :state="header.state" 
          :states="states" 
          class="form-header__state form-header__item" 
          v-if="header && header.state && header.state.name" 
        ></fts-state>
        <!-- history -->
        <div
          class="dv-form-history"
          :class="{'dv-form-history--count': header.commentCount}"
          v-if="isHistoryRight && header && !isMultiple"
        >
          <fts-tippy :content="`${isCommentRight ? 'Comment' : 'History'}`">
            <fts-btn
              text
              icon
              :color="header && (header.collaborationFlags & 4) === 4 ? 'primary' : 'default'"
              class="dv-form-header-action-btn"
              @click="toggleHistory"
            >
              <font-awesome-icon :icon="['far', 'comment-alt']" />
            </fts-btn>
          </fts-tippy>
          <div class="dv-form-history-count" v-if="header.commentCount">
            {{ header.commentCount > 99 ? 99 :header.commentCount }}
          </div>
        </div>
        <!-- close -->
        <fts-tippy content="Close">
          <fts-btn text icon @click="closeForm">
            <font-awesome-icon :icon="['fas', 'times']" />
          </fts-btn>
        </fts-tippy>
      </div>
    </div>

    <div class="list-view-form__content">
      <div class="form-content__form inistate-scroller" ref="form-content">
        <fts-transition-expand>
          <div 
            class="form-content__form-view"
            :class="{'form-content__form-view--activity-expand': isActivityViewForm}" 
            v-if="!selectedActivity || isActivityViewForm"
          >
            <div class="dv-form-error" v-if="viewFormError">
              <div class="dv-form-error-icon">
                <font-awesome-icon :icon="['fas', 'times']" fixed-width />
              </div>
              <div class="dv-form-error-title">
                Something went wrong
              </div>
              <div class="dv-form-error-description">
                {{viewFormError}}
              </div>
            </div>

            <fts-classification 
              :locus-id="header && header.id" 
              :vector-id="moduleId" 
              :users="users" 
              :username="username" 
              :activity-id="this.fullView ? 'view' : 'quickView'" 
              :view="true" 
              :view-data="viewData" 
              :loading="formLoading" 
              :form-settings="{
                hideLabelIcon: true
              }"
              v-else
            />

            <div class="form-content__form-view-footer">
              <div 
                class="form-content__form-collapse-btn" 
                @click="toggleActivityViewForm(false)" 
                v-if="selectedActivity && selectedActivity.id && isActivityViewForm"
              >Hide</div>
            </div>
          </div>
        </fts-transition-expand>

        <template v-if="selectedActivity && selectedActivity.id">
          <div 
            class="form-content__form-expand" 
            v-if="
              selectedActivity.id !== 'create' && 
              !isActivityViewForm && 
              !isError
            "
          >
            <fts-btn 
              text 
              text-transform-init 
              class="form-content__form-expand-btn" 
              @click="toggleActivityViewForm(true)"
            >show details</fts-btn>
          </div>

          <div class="dv-form-error" v-if="activityFormError">
            <div class="dv-form-error-icon">
              <font-awesome-icon :icon="['fas', 'times']" fixed-width />
            </div>
            <div class="dv-form-error-title">
              Something went wrong
            </div>
            <div class="dv-form-error-description">
              {{activityFormError}}
            </div>
          </div>

          <div 
            class="dv-form-activity" 
            v-else-if="['assign', 'changeStatus', 'duplicate', 'delete'].includes(selectedActivity.id)" 
          >
            <div class="dv-form-activity-content">
              <div class="dv-form-activity-label fts-text-center" v-if="selectedActivity.id == 'delete'">
                Are you sure to delete?
              </div>

              <div class="dv-form-activity-label fts-text-center" v-else-if="selectedActivity.id == 'duplicate'">
                Are you sure to duplicate?
              </div>

              <div class="dv-form-activity-state" v-else-if="selectedActivity.id == 'changeStatus'">
                <div class="dv-form-activity-label-group">
                  <div class="dv-form-activity-label">
                    Change state to
                  </div>

                  <fts-dropdown-select
                    append-to-body
                    prevent-overflow-alt-axis
                    prevent-overflow-tether-off
                    v-model="formData.selectedState"
                    :options="states"
                  >
                    <fts-state
                      slot="toggle"
                      slot-scope="{ selectedOption }"
                      :state="
                        selectedOption && selectedOption.name ?
                          selectedOption :
                          { name: 'select state' }
                      "
                      :states="states"
                    />

                    <div
                      slot="option"
                      slot-scope="{ option, select }"
                      class="dropdown-option"
                      @click="select(option)"
                    >
                      <fts-state :state="option" />
                    </div>

                  </fts-dropdown-select>
                </div>

                <div
                  class="fts-form-error"
                  v-if="
                    $v.formData &&
                    $v.formData.selectedState &&
                    $v.formData.selectedState.$error
                  "
                >
                  State is required.
                </div>
              </div>

              <fts-assign
                :assign-data="formData"
                v-else-if="selectedActivity.id == 'assign'"
              />
            </div>

            <fts-comment
              :vector-id="moduleId"
              :comment="comment"
              v-if="isCommentRight && selectedActivity.id !== 'duplicate'"
            />
          </div>

          <fts-classification 
            :locus-id="header && header.id" 
            :vector-id="moduleId" 
            :users="users" 
            :username="username" 
            :activity="selectedActivity.name" 
            :activity-id="selectedActivity.id" 
            :view="!isCapableOf(SYSTEM_CAPABILITY.Write)" 
            :comment="comment" 
            :comment-section="isCommentRight" 
            :loading="formLoading" 
            :form-settings="{
              hideLabelIcon: true
            }"
            @submit="submit" 
            ref="activityClassification" 
            v-else
          />
        </template>
      </div>
      
      <fts-history
        :key="header.id"
        :locus-id="header.id"
        :vector-id="moduleId"
        :states="states"
        :users="users"
        :comment-section="isCommentRight"
        :enable-notification="false"
        :init-open="getHistoryInitOpen()"
        class="form-content__history"
        @added="historyAdded"
        ref="ftsHistory"
        v-if="
          isHistoryRight &&
          !isMultiple &&
          !isError
        "
      />
    </div>

    <div class="list-view-form__footer" v-if="selectedActivity && selectedActivity.id">
      <fts-form-footer
        :activity="selectedActivity"
        :sync-state="$refs.activityClassification && $refs.activityClassification.syncState"
        :loading="$refs.activityClassification && $refs.activityClassification.isFormLoading"
        :submitting="formSubmitting"
        class=""
        @synchronize="evt => ($refs.activityClassification && $refs.activityClassification.synchronize(evt))"
        @sync:submit="$refs.activityClassification && $refs.activityClassification.submit()"
        @submit="submit"
        @cancel="cancelActivityForm"
      ></fts-form-footer>
    </div>

    <div class="list-view-form__footer" v-if="!formLoading && !isError && !selectedActivity">
      <fts-form-footer-action
        :activities="footer.activities"
        :customs="footer.customs"
        :documents="footer.documents"
        @execute:activity="evt => executeFormAction(evt, 'activity')"
        @execute:custom="evt => executeFormAction(evt, 'custom')"
        @execute:document="evt => executeFormAction(evt, 'template')"
        v-if="
          (
            (footer.activities && footer.activities.length) ||
            (footer.customs && footer.customs.length) ||
            (footer.documents && footer.documents.length)
          )
        "
      ></fts-form-footer-action>
    </div>

    <div class="list-view-form__overlay" v-if="formLoading || formSubmitting">
      <fts-spinner show />
    </div>

    <fts-form-message-modal ref="ftsFormMessageModal" />
  </div>
</template>

<script>
import Vue from 'vue';
import Vuelidate from 'vuelidate';
import { required } from 'vuelidate/lib/validators';
Vue.use(Vuelidate);
import moment from 'moment';

import commons from '../../commons/commons';
import { isCapableOf, SYSTEM_CAPABILITY } from '../../commons/flags';
import { classificationStore } from '../../store/classificationStore';
import { authorizationStore } from '../../store/authorizationStore';
import { clientStore } from '../../store/clientStore';
import LocalStorageService from '../../store/LocalStorageService';
import confetti from '../../commons/confetti';

import FTSBtn from '../FTSBtn';
import FTSClassification from '../FTSClassification';
import FTSTippy from '../FTSTippy';
import FTSProfile from '../FTSProfile';
import FTSFormSync from '../form/FTSFormSync';
import FTSHistory from '../history/FTSHistory';
import FTSFormFooter from '../form/FTSFormFooter';
import FTSFormMessageModal from '../form/FTSFormMessageModal';
import FTSComment from '../FTSComment';
import FTSDropdownSelect from '../FTSDropdownSelect';
import FTSState from '../FTSState';
import FTSFormFooterAction from '../form/FTSFormFooterAction';
import FTSTransitionExpand from '../FTSTransitionExpand';
import FTSSpinner from '../FTSSpinner';

const MODULE_UNAVAILABLE_MESSAGE = "Module you trying to access is unpublished or have been removed.";
const ENTRY_UNAVAILABLE_MESSAGE = "Entry you trying to access is not available. Please check with your administrator.";
const ACTIVITY_UNAVAILABLE_MESSAGE = "Entry not able to perform this activity. Please check with your administrator";
const CAPABILITY_UNAVAILABLE_MESSAGE = "401 Unauthorized. Access denied due to invalid credentials.";

const getSuccessMessage = (id, standard) => {
  if (standard) {
    if (id === 'create')
      return 'Successfully created';
    else if (id == 'edit')
      return 'Successfully updated';
    else if (id === 'delete')
      return 'Successfully deleted';
    else if (id === 'duplicate')
      return 'Successfully duplicated';
    else if (id === 'assign')
      return 'Successfully assigned';
  }
  return 'Submission successful';
}
const getErrorMessage = (id, standard) => {
  if (standard) {
    if (id === 'create')
      return 'Failed to create';
    else if (id === 'edit')
      return 'Failed to update';
    else if (id === 'delete')
      return 'Failed to delete';
    else if (id === 'duplicate')
      return 'Failed to duplicate';
    else if (id === 'assign')
      return 'Failed to assign';
  }
  return 'Submission failed';
}

export default {
  components: {
    'fts-btn': FTSBtn,
    'fts-classification': FTSClassification,
    'fts-tippy': FTSTippy,
    'fts-profile': FTSProfile,
    'fts-form-sync': FTSFormSync,
    'fts-history': FTSHistory,
    'fts-form-footer': FTSFormFooter,
    'fts-form-message-modal':FTSFormMessageModal,
    'fts-comment': FTSComment,
    'fts-dropdown-select': FTSDropdownSelect,
    'fts-state': FTSState,
    'fts-form-footer-action': FTSFormFooterAction,
    'fts-transition-expand': FTSTransitionExpand,
    'fts-spinner': FTSSpinner
  },
  data:() => ({
    SYSTEM_CAPABILITY: Object.freeze(SYSTEM_CAPABILITY),
    isShow: false,
    moduleId: null,
    listingId: null,
    header: {},
    footer: {},
    selectedActivity: null,
    username: null,
    fullView: false,
    viewData: null,
    isActivityViewForm: false,
    isActivityContinuable: false,
    expandFormAction: false,
    formData: {},
    comment: {},
    entries: [],
    items: [],
    activityFormNextStates: [],
    viewFormError: null,
    activityFormError: null,
    formLoading: false,
    formSubmitting: false,
    initialForm: null,
  }),
  validations() {
    let formData = {};

    if (this.selectedActivity && this.selectedActivity.id == 'changeStatus') {
      formData = {
        selectedState: {
          name: { required }
        }
      }
    }

    return { formData };
  },
  methods: {
    moment,
    momentRelativeTime(date) {
      return commons.momentRelativeTime(date);
    },
    calendarRelativeTime(date) {
      return commons.calendarRelativeTime(date);
    },
    isCapableOf(type) {
      return isCapableOf(type, clientStore.getters.workspaceModules, this.moduelId);
    },
    isRightOf(type) {
      return type &&
        this.footer &&
        this.footer.standards &&
        this.footer.standards.indexOf(type) > -1;
    },
    resetContentScroll() {
      this.$refs["form-content"].scrollTo({ top: 0, left: 0 });
    },
    setRouter() {
      const setRouteQuery = (query) => {
        if (this.fullView) {
          query.m = "f";
        } else if (query.hasOwnProperty("m")) {
          delete query.m;
        }
      }

      if (this.selectedActivity && this.selectedActivity.id === 'create') {
        let query = {
          ...this.$route.query,
          a: 'create'
        };
        if (query.hasOwnProperty('id')) {
          delete query.id;
        }
        this.$router.push({ query: query });
      } else if (this.header && this.header.id) {
        let query = {
          ...this.$route.query,
          id: commons.queryEncodeids(this.moduleId, this.header.id)
        }

        setRouteQuery(query);

        if (this.selectedActivity) {
          query.a = this.selectedActivity.id;
        } else if (query.hasOwnProperty("a")) {
          delete query.a;
        }

        this.$router.push({ query: query });
      } else {
        let route = {
          name: 'list-view',
          params: {
            moduleId: commons.encodeids(this.moduleId)
          }
        }
        if (this.listingId) {
          route.query = { l: this.listingId };
        } 
        this.$router.push(route);
      }
    },
    setEntryHeader(data) {
      if (Array.isArray(data) && data.length > 0 && commons.isObject(data[0])) {
        this.header = data[0];
        this.entries = data;
      } else if (commons.isObject(data)) {
        this.header = data;
        this.entries = [data];
      } else {
        this.header = {};
        this.entries = [];
      }
    },
    initialFormData() {
      this.formData = {
        id: this.header.id,
        assignees: this.header.assignees,
        due: this.header.due
      }
    },
    async loadViewForm() {
      this.formLoading = true;
      try {
        if (!this.isCapableOf(SYSTEM_CAPABILITY.Read)) {
          throw { response: { data: { message: CAPABILITY_UNAVAILABLE_MESSAGE }, status: 400 }};
        }
        const res = await classificationStore.dispatch('loadViewForm', {
          moduleId: this.moduleId ? this.moduleId.toString() : null,
          listingId: this.listingId,
          activityId: this.fullView ? 'view' : 'quickView',
          entryId: this.header.id,
          locusId: this.header.id,
          query: this.$route.query,
          users: this.users
        });

        if (this.header.id != res.header.entryId) {
          console.warn(`Form view aborted: Entry mismatch detected. Expected document ID '${this.header.documentId}', but received '${res.header.documentId}'.`);
          return;
        }

        this.header = res.header;
        this.footer = res.footer;
        this.viewData = res.viewData;
        !this.isActivityViewForm && this.initialFormData();
        this.items = [{
          ...res.header, 
          extension: {...res.viewData && res.viewData.formData}
        }];

        if (Array.isArray(this.footer && this.footer.standards)) {
          let excludedStandards = [];

          if (!this.isCapableOf(SYSTEM_CAPABILITY.Write)) {
            excludedStandards.push(...["create", "edit", "changeStatus", "duplicate", "assign"]);
          }

          if (!this.isCapableOf(SYSTEM_CAPABILITY.Delete)) {
            excludedStandards.push("delete");
          }

          this.footer.standards = this.footer.standards.filter(x => !excludedStandards.includes(x));
        }

        if (Array.isArray(this.footer && this.footer.customs) && this.footer.customs.length) {
          this.footer.customs = commons.getFormAvailableCustoms(this.footer.customs, this.items);
        }

        this.viewFormError = null;
      } catch(err) {
        console.error('loadViewForm', err);
        if (
          err.response &&
          err.response.status == 400 &&
          err.response.data &&
          err.response.data.message
        ) {
          this.viewFormError = err.response.data.message;
        } else {
          this.viewFormError = ENTRY_UNAVAILABLE_MESSAGE;
        }
      } finally {
        this.formLoading = false;
      }
    },
    async loadActivityForm() {
      this.$v.$reset();
      this.comment = {};
      this.activityFormNextStates = [];
      this.formLoading = true;
      this.isActivityViewForm = false;
      try {
        if (this.selectedActivity.id === "manage" && !this.isAdminUser) {
          throw { response: { data: { message: ACTIVITY_UNAVAILABLE_MESSAGE }, status: 400 }};
        }

        if (
          (this.selectedActivity.id === "delete" && !this.isCapableOf(SYSTEM_CAPABILITY.Delete)) ||
          (
            ["create", "edit", "changeStatus", "duplicate", "assign"].indexOf(this.selectedActivity.id) > -1 &&
            !this.isCapableOf(SYSTEM_CAPABILITY.Write)
          )
        ) {
          throw { response: { data: { message: CAPABILITY_UNAVAILABLE_MESSAGE }, status: 400 }};
        }

        const setForm = (result) => {
          this.header = result.header;
          this.footer = result.footer;
          this.initialFormData();
          // activity
          if (result.header && result.header.activity) {
            this.selectedActivity = result.header.activity;
          }
          // form sync
          if (result.needSync && this.selectedActivity) {
            this.$refs.activityClassification.pollData(this.selectedActivity);
          }
          // states
          if (this.states && this.states.length) {
            if (Array.isArray(this.footer.states) && this.footer.states.length) {
              this.activityFormNextStates = this.footer.states.map(x => this.states.find(y => y.id == x)).filter(y => y);
            }
          }
          // assignees
          if (Array.isArray(this.footer.assignees) && this.footer.assignees.length) {
            this.formData.assignees = this.footer.assignees;
          }
        }

        let payload = {
          moduleId: this.moduleId ? this.moduleId.toString() : null,
          listingId: this.listingId,
          activityId: this.selectedActivity.id,
          entryId: this.header.id,
          locusId: this.header.id,
          query: this.$route.query,
          users: this.users,
          enableCache: true,
          hasCache: false
        }

        if (this.selectedActivity.id == "create") {
          const cachedForm = classificationStore.getters.getCachedForm({
            moduleId: this.moduleId,
            activityId: this.selectedActivity.id
          });

          if (cachedForm) {
            classificationStore.dispatch("loadActivityFormCache", {
              setup: payload,
              data: cachedForm
            });
            setForm(cachedForm);
            payload.hasCache = true;
          }
        }

        if (payload.hasCache) {
          this.activityFormError = null;
          this.formLoading = false;
        }

        const result = await classificationStore.dispatch('loadActivityForm', payload);
        if (this.header.id != result.header.entryId) {
          console.warn(`Form activity aborted: Entry mismatch detected. Expected document ID '${this.header.documentId}', but received '${result.header.documentId}'.`);
          return;
        }

        setForm(result);

        if (result.needCache) {
          classificationStore.dispatch("setCachedForm", {
            moduleId: this.moduleId,
            activityId: this.selectedActivity.id,
            data: result
          });
        } else {
          classificationStore.dispatch("clearCachedForm", {
            moduleId: this.moduleId,
            activityId: this.selectedActivity.id
          });
        }

        this.activityFormError = null;
        this.formLoading = false;
      } catch(err) {
        console.error('loadActivityForm', err);

        if (axios.isCancel(err)) {
          return;
        }

        if (
          err.response &&
          err.response.status == 400 &&
          err.response.data &&
          err.response.data.message
        ) {
          this.activityFormError = err.response.data.message;
        } else {
          this.activityFormError = ACTIVITY_UNAVAILABLE_MESSAGE;
        }

        this.formLoading = false;
      }
    },
    getHistoryInitOpen() {
      const preference = LocalStorageService.getModuleListingPreference(this.moduleId, this.listingId);
      if (preference && preference.hasOwnProperty("historyVisible")) {
        return preference.historyVisible;
      }

      return this.header && this.header.commentCount && this.header.commentCount > 0;
    },
    toggleHistory() {
      if (this.$refs.ftsHistory) {
        this.$refs.ftsHistory.toggleHistory();
        LocalStorageService.setModuleListingPreference(
          this.moduleId,
          this.listingId,
          {
            ...LocalStorageService.getModuleListingPreference(this.moduleId, this.listingId),
            historyVisible: Boolean(this.$refs.ftsHistory.show)
          }
        );
      }
    },
    historyAdded() {
      if (this.header.commentCount) {
        this.header.commentCount += 1;
      } else {
        this.header.commentCount = 1;
      }
      this.$emit('refresh-list');
    },
    async submit(formData) {
      this.$v.$touch();

      if (this.$v.$invalid) return;

      try {
        this.formSubmitting = true;

        const result = await classificationStore.dispatch('submitActivity', {
          moduleId: (this.module && this.module.id)? this.module.id.toString() : null,
          activityId: this.selectedActivity.id,
          payload: formData,
          entryIds: this.entries.map((x) => x.id),
          post: this.comment,
          intention: this.comment.attention,
          state: this.formData.selectedState && this.formData.selectedState.name ? this.formData.selectedState.name : null,
          assignees: this.formData.assignees,
          due: this.formData.due ? moment(this.formData.due).endOf('day') : null,
          query: this.$route.query
        });

        this.formSubmitting = false;

        // confetti
        if (result.data.celebrate) {
          confetti();
        }
        // message
        if (result.data.message) {
          await this.$refs.ftsFormMessageModal.show(result.data.message);
        } else {
          this.$toasted.success(
            getSuccessMessage(this.selectedActivity.id, this.selectedActivity.standard)
          );
        }
        // form ads
        if (
          classificationStore.state.form &&
          classificationStore.state.form.ads
        ) {
          let entryId = result.data.id;
          try {
            eval(classificationStore.state.form && classificationStore.state.form.ads);
          } catch (ex) {
            this.$toasted.error(ex);
          }
        }

        window.eventBus.$emit("entry:updated", {
          moduleId: this.moduleId
        });

        this.closeForm();
      } catch(err) {
        console.error('submit', err);
        let message = getErrorMessage(this.selectedActivity.id, this.selectedActivity.standard);
        if (
          err.response &&
          err.response.data &&
          err.response.data.message
        ) {
          message = `${message}. ${err.response.data.message}`;
        }
        this.formSubmitting = false;
        this.$toasted.error(message);
      }
    },
    closeForm() {
      let query = {};
      if (this.$route.query.l) query.l = this.$route.query.l;
      if (this.$route.query.t) query.t = this.$route.query.t;
      this.$router.push({ query: query });

      Object.assign(this.$data, this.$options.data.apply(this));
      this.isShow = false;
    },
    cancelActivityForm() {
      if (this.initialForm == "activity") {
        this.closeForm();
      } else {
        this.selectedActivity = null;

        if (this.fullView === true && !this.isViewRight) {
          this.fullView = false;
        } else if (this.fullView !== true && !this.isQuickViewRight) {
          this.fullView = true;
        }

        this.setEntryHeader(this.header);
        this.setRouter();
        this.loadViewForm();
      }
    },
    executeFormAction(action, actionType) {
      if (actionType == "standard" || actionType == "activity") {
        this.selectedActivity = action;

        if (this.selectedActivity.id == "create") {
          this.setEntryHeader();
          this.footer = {};
        }

        this.setRouter();
        this.loadActivityForm();
      } else if (actionType == "custom") {
        commons.executeEntryCustom({
          action: action,
          items: this.items,
          modal: this.$refs.ftsActionPageModal
        });
      } else if (actionType == "document" || actionType == "template") {
        if (action.hasForm) {
          this.$refs.ftsDocumentFormModal.show({
            moduleId: this.moduleId,
            listingId: this.listingId,
            document: action,
            items: this.items
          });
        } else {
          window.eventBus.$emit("showPDFDocument", {
            document: action,
            items: [{ id: this.header.id }],
            moduleId: this.moduleId,
          });
        }
      } else {
        console.warn(`Unhandled action type: ${actionType}`);
      }
    },
    toggleActivityViewForm(value) {
      this.isActivityViewForm = value;
      this.resetContentScroll();
    }
  },
  computed: {
    isAdminUser() {
      return authorizationStore.getters.isAdminUser;
    },
    users() {
      return clientStore.getters.workspaceUsers;
    },
    states() {
      return this.designMode ?
        canvasDesignStore.getters.canvasStates :
        clientStore.getters.workspaceStates.filter((x) => x.module == this.moduleId);
    },
    module() {
      return clientStore.getters.workspaceModules.find((x) => x.id == this.moduleId);
    },
    moduleName() {
      return this.module && this.module.name;
    },
    isQuickViewRight() {
      return this.isRightOf('quickView');
    },
    isViewRight() {
      return this.isRightOf('view');
    },
    isCreateRight() {
      return this.isRightOf('create');
    },
    isCommentRight() {
      return this.isRightOf('comment');
    },
    isHistoryRight() {
      return this.isRightOf('history');
    },
    isAssignRight() {
      return this.isRightOf('assign');
    },
    isShareRight() {
      return this.isRightOf('share');
    },
    isChangeStatusRight() {
      return this.isRightOf('changeStatus');
    },
    isMultiple() {
      return this.entries && this.entries.length > 1;
    },
    isError() {
      return Boolean(
        (!Boolean(this.selectedActivity) && this.viewFormError) ||
        (Boolean(this.selectedActivity) && this.activityFormError)
      );
    },
    formActions() {
      let list = [];
      const addItems = (items, actionType) => {
        if (items && items.length) {
          const [lastItem] = list.slice(-1);

          if (lastItem && lastItem.actionType !== actionType && list.length) {
            list.push({
              id: `action-divider-${list.length}`,
              optionRole: "divider"
            });
          }

          const mappedItems = items.map(x => ({...x, actionType}));
          list = list.concat(mappedItems);
        }
      }

      if (!this.selectedActivity && this.footer) {
        addItems(this.footer.activities, "activity");
        addItems(this.footer.documents, "template");
        addItems(this.footer.customs, "custom");

        if (this.footer.standards && this.footer.standards.length) {
          let standards = commons.standardActivities.filter((x) =>
            this.footer.standards.indexOf(x.id) > -1 &&
            x.id != "view" &&
            x.id != "create" &&
            x.id != "changeStatus" &&
            x.id != "assign"
          );

          addItems(standards, "standard");
        }
      }

      if (this.isShareRight) {
        addItems([{id: "copyLink"}], "standard");
        addItems([{id: "share"}], "standard");

        if (authorizationStore.getters.isInternal) {
          addItems([{id: "embedForm"}], "standard");
        }
      }

      if (!this.selectedActivity && this.isAdminUser) {
        const availableAdminFeatures = ["manage"];
        let adminFeatures = commons.adminFeatures.filter(x => availableAdminFeatures.includes(x.id));
        addItems(adminFeatures, "standard");
      }

      return list;
    },
    formTitle() {
      if (
        (this.selectedActivity && this.selectedActivity.id !== 'create') ||
        !this.selectedActivity
      ) {
        if (this.isMultiple) {
          return `${this.entries.length} items selected`;
        } else if (this.header && this.header.title) {
          return this.header.title;
        } else if (!this.formLoading) {
          return "(untitled)";
        }
      }
    },
    selectedItemsEtc() {
      if (!this.entries || this.entries < 2) return "";
      else if (this.entries.length == 2) {
        return `${this.entries[0].documentId} and ${this.entries[1].documentId}`;
      } else if (this.entries.length == 3) {
        return `${this.entries[0].documentId}, ${this.entries[1].documentId} and ${this.entries[2].documentId}`;
      } else {
        return `${this.entries[0].documentId}, ${this.entries[1].documentId} and ${this.entries.length - 2} others`;
      }
    },
  },
  mounted() {
    classificationStore.dispatch("resetActivityForm");
    window.eventBus.$on('list-view-form:open', async (res) => {
      const current = {
        id: this.header && this.header.id ? this.header.id : null,
        activityId: this.selectedActivity ? this.selectedActivity.id : null,
        moduleId: this.moduleId,
        fullView: this.fullView
      }

      if (
        res.fromUrl &&
        res.id == current.id &&
        res.activityId == current.activityId &&
        (!res.moduleId || res.moduleId == current.moduleId)
      ) {
        return;
      }

      if (this.isShow) {
        this.resetContentScroll();
      }

      this.viewFormError = null;
      this.activityFormError = null;
      this.moduleId = res.moduleId;
      this.listingId = res.listingId;
      this.username = res.username;
      this.fullView = Boolean(res.fullView);

      if (Array.isArray(res.entries) && res.entries.length) {
        this.setEntryHeader(res.entries);
      } else {
        this.setEntryHeader(res);
      }

      this.initialFormData();

      this.isShow = true;

      if (res.activityId && res.activityId !== 'view' && res.activityId !== 'quickView') {
        this.selectedActivity = commons.standardActivities.find(x => x.id == res.activityId);
        this.viewData = null;
        
        if (!this.selectedActivity) {
          this.selectedActivity = commons.adminFeatures.find(x => x.id == res.activityId);
        }
        
        if (!this.selectedActivity) {
          this.selectedActivity = { id: res.activityId };
        }
      } else {
        this.selectedActivity = null;
      }

      this.setRouter();

      if (this.selectedActivity && this.selectedActivity.id) {
        this.initialForm = "activity";
        await this.loadActivityForm();
      } else {
        await this.loadViewForm();
      }
    });
    window.eventBus.$on("list-view-form:close", () => {
      if (this.show) {
        this.closeForm();
      }
    });
  },
  beforeDestroy() {
    window.eventBus.$off("list-view-form:open");
    window.eventBus.$off("list-view-form:close");
  }
}
</script>

<style lang="scss">
@use "./src/assets/scss/vars.scss" as *;

.fts-list-view-form {
  position: fixed;
  top: 56px;
  bottom: 0;
  width: 60%;
  max-width: 60%;
  min-width: 600px;
  right: 0;
  background-color: #fff;
  display: flex;
  flex-direction: column;
  box-shadow: var(--fts-elevation-5);
  .list-view-form__header {
    display: flex;
    align-items: flex-start;
    padding: 1rem;
    border-bottom: 1px solid var(--fts-border);
  }
  .list-view-form__content {
    flex: 1;
    display: flex;
    overflow: hidden;
  }
  .list-view-form__footer {
    .fts-form-footer {
      padding: 1rem;
    }
  }
  .list-view-form__overlay {
    @include flex-center-middle;
    position: absolute;
    height: 100%;
    width: 100%;
    background-color: transparent;
    z-index: 1;
    cursor: wait;
  }

  .form-header__left {
    flex :1;
  }
  .form-header__right {
    margin-left: 1rem;
    display: flex;
    align-items: center;
    .form-header__item {
      margin-right: 0.5rem;
      & + .form-header__item {
        margin-left: 0.5rem;
      }
    }
  }
  .form-header__uppr {
    display: flex;
    align-items: center;
    font-weight: var(--inistate-font-weight-bold);
    font-size: var(--inistate-heading-text-16);
    line-height: var(--inistate-heading-text-16--line-height);
    & + .form-header__lowr {
      margin-top: 0.25rem;
    }
  }
  .form-header__lowr {
    display: flex;
    align-items: center;
  }
  .form-header__activity {
    @include text-overflow-ellipsis;
    & + .form-header__module {
      &::before {
        content: "";
        height: var(--inistate-heading-text-16);
        margin-left: 0.375rem;
        margin-right: 0.375rem;
        border-left: 1px solid var(--inistate-text-secondary-color);
      }
    }
  }
  .form-header__module {
    @include text-overflow-ellipsis;
  }
  .form-header__audit {
    font-size: var(--inistate-label-text-12);
    line-height: var(--inistate-label-text-12--line-height);
  }
  .form-header__state {
  }
  .form-header__sync {
  }

  .form-content__form {
    flex: 1;
    padding: 1rem;
    .fts-form-element.compact-form-design > .fts-form-element-wrapper {
      flex-wrap: wrap;
    }

    .fts-form-label.form-label--icon-right,
    .fts-form-label.form-label--left {
      min-width: 100%;
      max-width: 100%;
      width: 100%;
      margin-right: 0;
      padding-left: 0;
      padding-right: 0;
      text-align: left;
      flex-direction: row;
      .form-label__icon {
        margin-left: 0;
        margin-right: 0.375rem;
      }
      .form-label__label {
        text-align: left;
      }
    }

    .fts-form-control,
    .fts-dropdown__select .vs-toggle {
      border-radius: var(--inistate-rounded-4);
    }

    .fts-display:not(.form-display-borderless) {
      > .fts-display-form-control,
      > .display-content {
        padding-left: 0;
        padding-right: 0;
      }
    }
  }
  .form-content__form-view {
    &.form-content__form-view--activity-expand {
      margin-bottom: 1rem;
      border-bottom: 1px solid var(--fts-border);
      // padding: 1rem;
      // border-radius: var(--inistate-rounded-4);
      // background-color: rgba(var(--inistate-color-neutral-50-rgb), 0.7);
    }
  }
  .form-content__form-view-footer {
    display: flex;
    justify-content: flex-end;
    margin-top: 0.75rem;
    margin-bottom: 1rem;
  }
  .form-content__form-collapse-btn {
    font-size: var(--inistate-label-text-14);
    line-height: var(--inistate-paragraph-text-14--line-height);
    color: var(--inistate-color-primary-500);
    &:hover {
      cursor: pointer;
      color: var(--inistate-color-primary-700);
    }
  }
  .form-content__form-expand {
    display: flex;
    justify-content: center;
    margin-bottom: 1rem;
  }
  .form-content__form-expand-btn {
    font-weight: var(--inistate-font-weight-light);
    font-size: var(--inistate-label-text-14);
    line-height: var(--inistate-paragraph-text-14--line-height);
  }
  
  .fts-history {
    &.form-content__history {
      flex: 1 1 400px;
      max-width: 400px;
      min-width: 400px;
    }
  }

  .dv-form-history {
    &.dv-form-history--count {
      position: relative;
      padding-right: 0.625rem;
    }
  }
  .dv-form-history-count {
    @include absolute-center-y;
    left: 1.625rem;
    min-width: 1rem;
    padding: 0 0.125rem;
    border: 1px solid transparent;
    border-radius: 10em;
    font-size: 0.75rem;
    color: var(--fts-default);
    text-align: center;
    transition: all 0.3s ease-in-out;
  }
  .dv-form-activity-content {
    margin-bottom: 1.5rem;
  }
  .dv-form-activity-label {
    font-size: 1rem;
    line-height: 1.5rem;
    color: var(--fts-text);
  }
  .dv-form-activity-label-group {
    display: flex;
    align-items: center;
    .dv-form-activity-label {
      margin-right: 0.5rem;
    }
  }
  .dv-form-activity-state {
    display: flex;
    flex-direction: column;
    align-items: center;
  }
  .dv-form-error {
    padding: 2rem;
    text-align: center;
  }
  .dv-form-error-icon {
    height: 80px;
    width: 80px;
    display: flex;
    align-items: center;
    justify-content: center;
    margin: 0 auto 1.25rem;
    border: 2px solid var(--fts-danger);
    border-radius: 50%;
    color: var(--fts-danger);
    > svg {
      color: inherit;
      font-size: 2.5rem;
    }
  }
  .dv-form-error-title {
    margin-bottom: 0.75rem;
    color: var(--fts-danger);
    font-weight: 500;
    font-size: 1.25rem;
    line-height: 1.75rem;
    word-break: break-word;
  }
  .dv-form-error-description {
    color: var(--fts-default);
    font-size: 0.875rem;
    line-height: 1.25rem;
    word-break: break-word;
  }
}
</style>